/**
 * ValouFeedbackToast - Micro-Umfrage nach Favorit-Hinzufuegen
 *
 * Zeigt eine optionale Frage an, wofuer der User den Ort nutzt.
 * Multi-Select moeglich. Verschwindet nach 10 Sekunden automatisch.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, Heart } from 'lucide-react';
import { VALOU_FEEDBACK_LABELS } from '../../types';
import type { ValouAreaId } from '../../types/valou';

interface ValouFeedbackToastProps {
  placeName: string;
  onSubmit: (areas: ValouAreaId[]) => void;
  onDismiss: () => void;
  autoHideDelay?: number; // ms, default 10000
}

export function ValouFeedbackToast({
  placeName,
  onSubmit,
  onDismiss,
  autoHideDelay = 10000,
}: ValouFeedbackToastProps) {
  const [selectedAreas, setSelectedAreas] = useState<ValouAreaId[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  // Animate in
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Auto-hide after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, autoHideDelay);

    return () => clearTimeout(timer);
  }, [autoHideDelay]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setTimeout(onDismiss, 200); // Wait for animation
  }, [onDismiss]);

  const handleToggleArea = (areaId: ValouAreaId) => {
    setSelectedAreas((prev) =>
      prev.includes(areaId)
        ? prev.filter((id) => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleSubmit = () => {
    if (selectedAreas.length > 0) {
      onSubmit(selectedAreas);
    }
    handleDismiss();
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 max-w-md transition-all duration-200 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-green-50 border-b border-green-100">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
            <span className="text-sm font-medium text-gray-800">
              Zu Favoriten hinzugefuegt
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-green-100 rounded transition"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-3">
            Wofuer nutzt du diesen Ort?
          </p>

          {/* Chip Grid */}
          <div className="flex flex-wrap gap-2 mb-4">
            {VALOU_FEEDBACK_LABELS.map((item) => {
              const isSelected = selectedAreas.includes(item.areaId);
              return (
                <button
                  key={item.areaId}
                  onClick={() => handleToggleArea(item.areaId)}
                  className={`
                    relative px-3 py-1.5 rounded-full text-sm font-medium
                    transition-all duration-150 border-2
                    ${
                      isSelected
                        ? 'text-white border-transparent'
                        : 'text-gray-700 border-gray-200 bg-gray-50 hover:border-gray-300'
                    }
                  `}
                  style={{
                    backgroundColor: isSelected ? item.color : undefined,
                    borderColor: isSelected ? item.color : undefined,
                  }}
                >
                  <span className="flex items-center gap-1.5">
                    {isSelected && <Check className="w-3.5 h-3.5" />}
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={selectedAreas.length === 0}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition
                ${
                  selectedAreas.length > 0
                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Fertig
            </button>
          </div>
        </div>

        {/* Progress bar for auto-hide */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-green-500 transition-all ease-linear"
            style={{
              animation: `shrink ${autoHideDelay}ms linear forwards`,
            }}
          />
        </div>
      </div>

      {/* CSS for progress bar animation */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

export default ValouFeedbackToast;
